import { CorsService } from './corsService';
import { JWTService } from './jwtService';

/**
 * CareDaily "GoIcon Goal Activity" endpoint wrapper.
 *
 * CareDaily note: "You can submit goal activity to CareDaily the same way you submit log activity.
 * The endpoint is goicon/goalActivity."
 *
 * The exact payload contract may vary by CareDaily deployment; this service is implemented as a
 * typed pass-through. We keep it best-effort so it never breaks core goal CRUD if the endpoint
 * rejects an unexpected payload.
 */
export class GoalActivityService {
  private static getApiUrl(envOverride?: 'sandbox' | 'production'): string {
    const withEnv = (baseUrl: string) => {
      if (!envOverride) return baseUrl;
      return baseUrl.includes('?')
        ? `${baseUrl}&env=${encodeURIComponent(envOverride)}`
        : `${baseUrl}?env=${encodeURIComponent(envOverride)}`;
    };

    const proxyUrl = process.env.REACT_APP_PROXY_URL;
    if (proxyUrl && proxyUrl !== 'https://wellness.goicon.co') {
      return withEnv(`${proxyUrl}/api/goicon-goal`);
    }

    if (
      process.env.NODE_ENV === 'development' ||
      window.location.hostname.includes('netlify.app') ||
      window.location.hostname.includes('goicon.co')
    ) {
      const explicitBase = process.env.REACT_APP_FUNCTIONS_BASE;
      if (explicitBase) {
        return withEnv(`${explicitBase.replace(/\/$/, '')}/goicon-goal-proxy`);
      }
      return withEnv('/.netlify/functions/goicon-goal-proxy');
    }

    // Fallback: direct call (may have CORS issues; also would require exposing API key)
    console.warn('⚠️ Using direct CareDaily goalActivity endpoint (may have CORS/auth issues)');
    const useSandbox = envOverride === 'sandbox';
    return useSandbox
      ? 'https://sboxall.peoplepowerco.com/espapi/integration/goicon/goalActivity'
      : 'https://app.caredaily.ai/espapi/integration/goicon/goalActivity';
  }

  /**
   * Best-effort goal activity submission.
   * Caller provides payload; this service posts it to the Netlify proxy.
   */
  static async submit(
    payload: Record<string, any>,
    options?: { envOverride?: 'sandbox' | 'production' }
  ): Promise<{ success: boolean; data?: any }> {
    const envOverride = options?.envOverride;
    const apiUrl = this.getApiUrl(envOverride);

    // The goicon-goal-proxy verifies this token and resolves the facility's ESP
    // key server-side; without it the proxy returns 401.
    const token = JWTService.getToken();
    const response = await CorsService.corsEnabledFetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(envOverride ? { 'X-CareDaily-Env': envOverride } : {}),
      },
      body: JSON.stringify(payload),
    });

    const json = await response.json().catch(() => ({}));

    if (!response.ok) {
      const baseMessage = (json && (json.error || json.message)) || `HTTP ${response.status}: goalActivity failed`;
      const details = json?.originalResponse ? ` | upstream=${JSON.stringify(json.originalResponse).slice(0, 300)}` : '';
      throw new Error(`${baseMessage}${details}`);
    }

    return json;
  }
}

